import { Component, inject, Inject } from '@angular/core';
import { switchMap, take, tap } from 'rxjs/operators';
import { AbstractModal } from '../abstract-modal/abstract-modal';
import {
  PaymentMethodDeletionBlockedError,
  PaymentMethodSurface,
  PaymentMethodTrackService,
  PaymentMethodWorkflowEvent,
  PaymentPlatformService,
  VALIDATION_BLOCKED_ERROR_CATEGORY,
} from '@aiwyn/common/payments';
import { AuthStateAdaptorService } from '@aiwyn/auth';
import { extractFailureContext } from '@aiwyn/shared';
import { BehaviorSubject, of } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EngagementApiService } from '@aiwyn/api';
import { Store } from '@ngxs/store';
import { SettingsState } from '../../settings/state';
import { PaymentPlanService } from '../../../../services/payment-plan/payment-plan.service';

export interface ConfirmDeleteComponentData {
  isAch: boolean;
  paymentMethodId: string;
  /**
   * Type discriminator for the method being removed (`'CARD'` or `'ACH'`). Stamped on every
   * PAYMENT_METHOD_REMOVE_* event so dashboards can slice card-vs-bank removal funnels without
   * walking back to the opener's `REMOVE_STARTED` row.
   */
  paymentMethodType: string;
  /**
   * Where the remove flow was opened from. Stamped on every PAYMENT_METHOD_* event via the
   * per-container `PaymentMethodTrackService` envelope.
   */
  surface: PaymentMethodSurface;
}

@Component({
  selector: 'app-confirm-delete',
  templateUrl: './confirm-delete.component.html',
  styleUrls: ['./confirm-delete.component.scss'],
  standalone: false,
  providers: [PaymentMethodTrackService],
})
export class ConfirmDeleteComponent extends AbstractModal {
  requestProcessing$ = new BehaviorSubject<boolean>(false);

  private readonly paymentMethodTrackService = inject(PaymentMethodTrackService);
  private readonly authStateAdaptor = inject(AuthStateAdaptorService);

  // Set to true once a terminal PAYMENT_METHOD_* event has fired (REMOVE_SUCCEEDED or any of the
  // *_FAILED variants). afterClosed reads this to decide whether to emit PAYMENT_METHOD_REMOVE_CANCELLED.
  private _didEmitTerminalEvent = false;

  constructor(
    public dialogRef: MatDialogRef<ConfirmDeleteComponent, boolean | PaymentMethodDeletionBlockedError>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDeleteComponentData,
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly engagementApiService: EngagementApiService,
    private readonly paymentPlanService: PaymentPlanService,
    private readonly store: Store,
  ) {
    super(dialogRef);

    this.paymentMethodTrackService.setSurface(this.data.surface);
    this.paymentMethodTrackService.setUserType(this.authStateAdaptor.getUserTypeSnapshot());

    // Catches every dismiss path (Cancel button, X, ESC, backdrop) in one place.
    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (!this._didEmitTerminalEvent) {
        this.paymentMethodTrackService.trackWorkflow(
          PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_CANCELLED,
          { paymentMethodId: this.data.paymentMethodId, paymentMethodType: this.data.paymentMethodType },
        );
      }
    });
  }

  /**
   * Close the modal.
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Cancel the delete (explicit Cancel button click).
   */
  cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Delete the payment method source.
   */
  delete(): void {
    const { paymentMethodId, paymentMethodType } = this.data;
    this.requestProcessing$.next(true);

    // Capture the default-payment-method-id at delete start so REMOVE_SUCCEEDED's `wasDefault`
    // reflects pre-delete state. The platform reshuffles the default after a successful delete
    // (see payment-platform.service `deletePaymentMethod`), so reading later would give the wrong
    // answer. NGXS selectors emit synchronously, so this populates before the pipeline below
    // subscribes — but capture-then-compare is the contract a future reader can rely on.
    let defaultPaymentMethodIdSnapshot: string | undefined;
    this.paymentPlatformService.getDefaultPaymentMethodId().pipe(take(1)).subscribe(
      id => { defaultPaymentMethodIdSnapshot = id; },
    );

    // Track which validation phase the pipeline is currently in so a thrown error can be attributed
    // to the correct FAILED step.
    let currentStage: 'domain-validation' | 'platform-validation' | 'remove' = 'domain-validation';
    const domainKey = `PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION:${paymentMethodId}`;
    const platformKey = `PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION:${paymentMethodId}`;
    const removeKey = `PAYMENT_METHOD_REMOVE:${paymentMethodId}`;

    this.paymentMethodTrackService.trackWorkflow(
      PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUBMITTED,
      { paymentMethodId, paymentMethodType },
    );
    this.paymentMethodTrackService.startOperation(domainKey);

    this.engagementApiService.isPaymentMethodUsedByEngagement(paymentMethodId).pipe(
      switchMap(isUsedByEngagements => {
        const isUsedByAutoPay = this.store.selectSnapshot(SettingsState.isMethodUsedForAutoPay(paymentMethodId));
        const isUsedByRecurringPlan = this.paymentPlanService.isPaymentMethodUsedByPaymentPlan(paymentMethodId);
        const isUsedByAutomaticPayments = isUsedByAutoPay || isUsedByRecurringPlan;
        const domainPassed = !isUsedByAutomaticPayments && !isUsedByEngagements;
        const domainDurationMs = this.paymentMethodTrackService.endOperation(domainKey);

        if (!domainPassed) {
          this._didEmitTerminalEvent = true;
          this.paymentMethodTrackService.trackWorkflow(
            PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_FAILED,
            {
              errorCategory: VALIDATION_BLOCKED_ERROR_CATEGORY,
              paymentMethodId,
              paymentMethodType,
              operationDurationMs: domainDurationMs,
              isUsedByAutomaticPayments,
              isUsedByEngagements,
            },
          );
          return of(new PaymentMethodDeletionBlockedError(isUsedByAutomaticPayments, isUsedByEngagements, false));
        }

        this.paymentMethodTrackService.trackWorkflow(
          PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUCCEEDED,
          { paymentMethodId, paymentMethodType, operationDurationMs: domainDurationMs },
        );

        this.paymentMethodTrackService.trackWorkflow(
          PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUBMITTED,
          { paymentMethodId, paymentMethodType },
        );
        this.paymentMethodTrackService.startOperation(platformKey);
        currentStage = 'platform-validation';

        return this.paymentPlatformService.deletePaymentMethod(paymentMethodId).pipe(
          tap(_ => {
            this.paymentMethodTrackService.trackWorkflow(
              PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUCCEEDED,
              { paymentMethodId, paymentMethodType, operationDurationMs: this.paymentMethodTrackService.endOperation(platformKey) },
            );
            // Once platform validation passes, the same network leg completes the removal.
            // Start the REMOVE timer immediately so the SUCCEEDED stamp reflects "post-validation"
            // duration — the implementation can later split if the leg is separated.
            this.paymentMethodTrackService.startOperation(removeKey);
            currentStage = 'remove';
          }),
          switchMap(_ => this.paymentPlatformService.getSelectedSavedPaymentMethodId()),
          switchMap(selectedSavedPaymentMethodId => selectedSavedPaymentMethodId === paymentMethodId
            ? this.paymentPlatformService.getDefaultPaymentMethodId().pipe(
              switchMap(defaultPaymentMethodId => {
                if (!defaultPaymentMethodId) {
                  return of(undefined);
                }
                return this.paymentPlatformService.setSelectedSavedPaymentMethodId(defaultPaymentMethodId);
              }),
            )
            : of(undefined)),
        );
      }),
      take(1),
    ).subscribe({
      next: result => {
        if (result instanceof PaymentMethodDeletionBlockedError) {
          // Domain validation already failed and tracked DOMAIN_VALIDATION_FAILED inline.
          this.dialogRef.close(result);
        } else {
          this._didEmitTerminalEvent = true;
          this.paymentMethodTrackService.trackWorkflow(
            PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_SUCCEEDED,
            {
              paymentMethodId,
              paymentMethodType,
              wasDefault: defaultPaymentMethodIdSnapshot === paymentMethodId,
              operationDurationMs: this.paymentMethodTrackService.endOperation(removeKey),
            },
          );
          this.dialogRef.close(true);
        }
      },
      error: error => {
        this.requestProcessing$.next(false);
        this._didEmitTerminalEvent = true;
        if (error instanceof PaymentMethodDeletionBlockedError) {
          // Platform validation failed (e.g. pending payments). Domain check already passed and was tracked.
          this.paymentMethodTrackService.trackWorkflow(
            PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_FAILED,
            {
              errorCategory: VALIDATION_BLOCKED_ERROR_CATEGORY,
              paymentMethodId,
              paymentMethodType,
              operationDurationMs: this.paymentMethodTrackService.endOperation(platformKey),
              isUsedByPendingPayments: error.isUsedByPendingPayments,
            },
          );
          this.dialogRef.close(error);
        } else {
          // Transport / unexpected failure at the validation or remove stage.
          const failureContext = extractFailureContext(error);
          if (currentStage === 'domain-validation') {
            this.paymentMethodTrackService.trackWorkflow(
              PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_FAILED,
              {
                ...failureContext,
                paymentMethodId,
                paymentMethodType,
                operationDurationMs: this.paymentMethodTrackService.endOperation(domainKey),
                isUsedByAutomaticPayments: false,
                isUsedByEngagements: false,
              },
            );
          } else if (currentStage === 'platform-validation') {
            this.paymentMethodTrackService.trackWorkflow(
              PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_FAILED,
              {
                ...failureContext,
                paymentMethodId,
                paymentMethodType,
                operationDurationMs: this.paymentMethodTrackService.endOperation(platformKey),
                isUsedByPendingPayments: false,
              },
            );
          } else {
            this.paymentMethodTrackService.trackWorkflow(
              PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_FAILED,
              {
                ...failureContext,
                paymentMethodId,
                paymentMethodType,
                operationDurationMs: this.paymentMethodTrackService.endOperation(removeKey),
              },
            );
          }
        }
      },
    });
  }
}
